// FILE: LinkedPullRequestBadge.tsx
// Purpose: The "this thread produced #123" mark, for the thread list.
// Layer: Component
// Exports: LinkedPullRequestBadge
//
// Reads the link the thread itself carries (`linkedPullRequest`), which is a recorded fact rather
// than a match on branch names — the reason the projection stores it at all. What it deliberately
// does not show is a *state*: the link is only an address, and open/merged/closed is GitHub's
// answer, read live by the Pull Requests section. A colour here would be a guess that goes stale
// the moment somebody merges.
//
// The stateful badge next to it, driven by `git.status`, keeps its colours precisely because it
// has just asked. This one appears when that one has nothing to say.

import type { ProjectId } from "@taskin-code/contracts";

import { GitPullRequestIcon } from "~/lib/icons";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

export function LinkedPullRequestBadge({
  number,
  projectId,
  onOpenSection,
  className,
}: {
  number: number;
  projectId: ProjectId;
  /** Absent for a reader without `p.pull_requests.view`, who has no section to be sent to. */
  onOpenSection?: ((projectId: ProjectId) => void) | undefined;
  className?: string;
}) {
  const label = `Pull request #${number}`;
  const content = (
    <>
      <GitPullRequestIcon className="size-3" />
      <span className="tabular-nums">#{number}</span>
    </>
  );
  const shared = cn(
    "inline-flex shrink-0 items-center gap-0.5 rounded-sm text-[10px] leading-4 text-muted-foreground/70",
    className,
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          onOpenSection ? (
            <button
              type="button"
              aria-label={label}
              className={cn(shared, "cursor-pointer outline-hidden hover:text-foreground/70")}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenSection(projectId);
              }}
            >
              {content}
            </button>
          ) : (
            <span aria-label={label} className={shared}>
              {content}
            </span>
          )
        }
      />
      <TooltipPopup side="top">
        {onOpenSection ? `${label} — open the Pull Requests section` : label}
      </TooltipPopup>
    </Tooltip>
  );
}
